import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = {
  primary: '#2b3380',
  whatsapp: '#25D366',
  success: '#4caf50',
  warning: '#ff9800',
  error: '#f44336',
  info: '#2196f3',
  purple: '#9c27b0'
};

const CHART_COLORS = ['#2b3380', '#4caf50', '#ff9800', '#f44336', '#2196f3', '#9c27b0'];

function WhatsAppAnalyticsAdvanced({ user, onLogout }) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [days, setDays] = useState(7);
  
  // Analytics data
  const [overview, setOverview] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [agentPerformance, setAgentPerformance] = useState(null);
  const [campaignPerf, setCampaignPerf] = useState(null);
  const [peakHours, setPeakHours] = useState(null);
  const [sentiment, setSentiment] = useState(null);
  const [responseTime, setResponseTime] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [overviewRes, engagementRes, agentRes, campaignRes, peakRes, sentimentRes, responseRes] = await Promise.all([
        axios.get(`${API}/api/whatsapp/analytics/overview?days=${days}`, config),
        axios.get(`${API}/api/whatsapp/analytics/customer-engagement?days=${days}`, config),
        axios.get(`${API}/api/whatsapp/analytics/agent-performance?days=${days}`, config),
        axios.get(`${API}/api/whatsapp/analytics/campaign-performance?days=${days}`, config),
        axios.get(`${API}/api/whatsapp/analytics/peak-hours?days=${days}`, config),
        axios.get(`${API}/api/whatsapp/analytics/conversation-sentiment?days=${days}`, config),
        axios.get(`${API}/api/whatsapp/analytics/response-time?days=${days}`, config)
      ]);

      setOverview(overviewRes.data);
      setEngagement(engagementRes.data);
      setAgentPerformance(agentRes.data);
      setCampaignPerf(campaignRes.data);
      setPeakHours(peakRes.data);
      setSentiment(sentimentRes.data);
      setResponseTime(responseRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: '📊 Overview', icon: '📊' },
    { id: 'campaigns', label: '📢 Campaigns', icon: '📢' },
    { id: 'agents', label: '👥 Agent Performance', icon: '👥' },
    { id: 'engagement', label: '💬 Customer Engagement', icon: '💬' },
  ];

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="text-6xl mb-4">📊</div>
            <p className="text-xl text-gray-600">Loading Analytics...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2" style={{ color: COLORS.primary }}>
            📊 WhatsApp Advanced Analytics
          </h1>
          <div className="flex items-center gap-4">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
              style={{ focusRingColor: COLORS.primary }}
            >
              <option value={7}>Last 7 Days</option>
              <option value={14}>Last 14 Days</option>
              <option value={30}>Last 30 Days</option>
              <option value={90}>Last 90 Days</option>
            </select>
            <button
              onClick={fetchAnalytics}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-300">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 font-medium transition ${
                activeTab === tab.id
                  ? 'border-b-4 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              style={activeTab === tab.id ? { borderColor: COLORS.primary } : {}}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard
                title="Total Conversations"
                value={overview?.overview?.total_conversations || 0}
                icon="💬"
                color={COLORS.primary}
              />
              <MetricCard
                title="Messages Sent"
                value={overview?.overview?.messages_sent || 0}
                icon="📤"
                color={COLORS.success}
              />
              <MetricCard
                title="Active Customers"
                value={overview?.overview?.active_customers || 0}
                icon="👥"
                color={COLORS.info}
              />
              <MetricCard
                title="Avg Response Time"
                value={`${overview?.overview?.avg_response_time || 0} min`}
                icon="⏱️"
                color={COLORS.warning}
              />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Sentiment Distribution */}
              {sentiment && (
                <div className="bg-white p-6 rounded-lg shadow">
                  <h3 className="text-lg font-semibold mb-4">😊 Customer Sentiment</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Positive', value: sentiment.sentiment_distribution.positive },
                          { name: 'Neutral', value: sentiment.sentiment_distribution.neutral },
                          { name: 'Negative', value: sentiment.sentiment_distribution.negative }
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        <Cell fill={COLORS.success} />
                        <Cell fill={COLORS.warning} />
                        <Cell fill={COLORS.error} />
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Peak Hours */}
              {peakHours && (
                <div className="bg-white p-6 rounded-lg shadow">
                  <h3 className="text-lg font-semibold mb-4">⏰ Peak Hours Activity</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={peakHours.hourly_distribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" label={{ value: 'Hour of Day', position: 'insideBottom', offset: -5 }} />
                      <YAxis label={{ value: 'Messages', angle: -90, position: 'insideLeft' }} />
                      <Tooltip />
                      <Bar dataKey="count" fill={COLORS.whatsapp} />
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-gray-600 mt-2 text-center">
                    🔥 Peak Hour: {peakHours.peak_hour}:00 ({peakHours.peak_hour_messages} messages)
                  </p>
                </div>
              )}
            </div>

            {/* Response Time Distribution */}
            {responseTime && (
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">⚡ Response Time Distribution</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{responseTime.distribution.under_5_min}</div>
                    <div className="text-sm text-gray-600">Under 5 min</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{responseTime.distribution['5_to_15_min']}</div>
                    <div className="text-sm text-gray-600">5-15 min</div>
                  </div>
                  <div className="text-center p-4 bg-yellow-50 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600">{responseTime.distribution['15_to_30_min']}</div>
                    <div className="text-sm text-gray-600">15-30 min</div>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">{responseTime.distribution.over_30_min}</div>
                    <div className="text-sm text-gray-600">Over 30 min</div>
                  </div>
                </div>
                <p className="text-center mt-4 text-lg">
                  Average Response Time: <span className="font-bold" style={{ color: COLORS.primary }}>
                    {responseTime.avg_response_time_minutes} minutes
                  </span>
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'campaigns' && campaignPerf && (
          <div className="space-y-6">
            {/* Campaign Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <MetricCard
                title="Total Campaigns"
                value={campaignPerf.summary.total_campaigns}
                icon="📢"
                color={COLORS.primary}
              />
              <MetricCard
                title="Total Recipients"
                value={campaignPerf.summary.total_recipients}
                icon="👥"
                color={COLORS.info}
              />
              <MetricCard
                title="Messages Sent"
                value={campaignPerf.summary.total_sent}
                icon="📤"
                color={COLORS.success}
              />
              <MetricCard
                title="Delivered"
                value={campaignPerf.summary.total_delivered}
                icon="✅"
                color={COLORS.whatsapp}
              />
              <MetricCard
                title="Delivery Rate"
                value={`${campaignPerf.summary.overall_delivery_rate}%`}
                icon="📊"
                color={COLORS.warning}
              />
            </div>

            {/* Campaign List */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold">Campaign Performance</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipients</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Delivered</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Failed</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Delivery Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {campaignPerf.campaigns.map((campaign) => (
                      <tr key={campaign.campaign_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{campaign.name}</div>
                          <div className="text-xs text-gray-500">
                            {new Date(campaign.created_at).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            campaign.status === 'completed' ? 'bg-green-100 text-green-800' :
                            campaign.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                            campaign.status === 'scheduled' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {campaign.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{campaign.recipients_count}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{campaign.sent}</td>
                        <td className="px-6 py-4 text-sm text-green-600 font-medium">{campaign.delivered}</td>
                        <td className="px-6 py-4 text-sm text-red-600">{campaign.failed}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                              <div
                                className="bg-green-500 h-2 rounded-full"
                                style={{ width: `${campaign.delivery_rate}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium">{campaign.delivery_rate}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'agents' && agentPerformance && (
          <div className="space-y-6">
            {/* Agent Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <MetricCard
                title="Total Agents"
                value={agentPerformance.total_agents}
                icon="👥"
                color={COLORS.primary}
              />
              <MetricCard
                title="Total Conversations Handled"
                value={agentPerformance.agents.reduce((sum, a) => sum + a.total_conversations, 0)}
                icon="💬"
                color={COLORS.info}
              />
              <MetricCard
                title="Avg Resolution Rate"
                value={`${(agentPerformance.agents.reduce((sum, a) => sum + a.resolution_rate, 0) / agentPerformance.total_agents || 0).toFixed(1)}%`}
                icon="✅"
                color={COLORS.success}
              />
            </div>

            {/* Agent Leaderboard */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold">🏆 Agent Leaderboard</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {agentPerformance.agents.map((agent, index) => (
                    <div key={agent._id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                      <div className="text-3xl font-bold" style={{ color: COLORS.primary }}>
                        #{index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-lg">{agent.agent_name}</div>
                        <div className="flex gap-6 mt-2 text-sm text-gray-600">
                          <span>💬 {agent.total_conversations} conversations</span>
                          <span>✅ {agent.resolved_count} resolved</span>
                          <span>📊 {agent.resolution_rate}% resolution rate</span>
                        </div>
                      </div>
                      {index === 0 && <div className="text-4xl">🥇</div>}
                      {index === 1 && <div className="text-4xl">🥈</div>}
                      {index === 2 && <div className="text-4xl">🥉</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'engagement' && engagement && (
          <div className="space-y-6">
            {/* Engagement Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard
                title="Total Conversations"
                value={engagement.total_conversations}
                icon="💬"
                color={COLORS.primary}
              />
              <MetricCard
                title="Active Customers"
                value={engagement.active_customers}
                icon="👥"
                color={COLORS.info}
              />
              <MetricCard
                title="Messages Received"
                value={engagement.messages_received}
                icon="📥"
                color={COLORS.success}
              />
              <MetricCard
                title="Satisfaction Rate"
                value={`${engagement.satisfaction_rate}%`}
                icon="😊"
                color={COLORS.warning}
              />
            </div>

            {/* Message Volume Chart */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">📊 Message Volume</h3>
              <div className="grid grid-cols-2 gap-8">
                <div className="text-center p-6 bg-green-50 rounded-lg">
                  <div className="text-4xl mb-2">📤</div>
                  <div className="text-3xl font-bold text-green-600">{engagement.messages_sent}</div>
                  <div className="text-gray-600 mt-2">Messages Sent</div>
                </div>
                <div className="text-center p-6 bg-blue-50 rounded-lg">
                  <div className="text-4xl mb-2">📥</div>
                  <div className="text-3xl font-bold text-blue-600">{engagement.messages_received}</div>
                  <div className="text-gray-600 mt-2">Messages Received</div>
                </div>
              </div>
              <div className="mt-6 text-center">
                <p className="text-lg">
                  Average Messages per Conversation:{' '}
                  <span className="font-bold" style={{ color: COLORS.primary }}>
                    {engagement.avg_messages_per_conversation}
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

// Metric Card Component
function MetricCard({ title, value, icon, color }) {
  return (
    <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold" style={{ color }}>{value}</p>
        </div>
        <div className="text-5xl opacity-20">{icon}</div>
      </div>
    </div>
  );
}

export default WhatsAppAnalyticsAdvanced;
